/**
 * createManualVisit — POST /api/projects/calllog/ for a manual / retroactive
 * visit entered via the ManualEntryPreflight wizard (rep forgot to start the
 * day, or is entering a past visit).
 *
 * Unlike the live `checkIn` op, a manual entry carries the FULL payload: the
 * drive-to leg, the return-drive leg, team-project / team-leader flags, and
 * free-text notes. Reusing `checkIn` here would silently drop all of those
 * (it hardcodes teamproject/teamleader=false and has no drive-back or notes),
 * so manual entry gets its own op (#2013).
 *
 * Mirrors the check-in lifecycle: applyLocal writes a visit_task_sessions row
 * with callLogId=null; reconcile stamps the server calllogid after sync so
 * dependent writes (survey/photo/checkout) resolve it via the visit localId.
 *
 * EMITTED BY: useVisitDay.injectManualVisit (store_checked_in at the arrival the
 * rep asserted, plus drive_ended for the drive it closes), called from
 * StoreDetail's manual-visit adoption. The hook owns it because the events must
 * be stamped at the ASSERTED instant and must land alongside the leg chain,
 * neither of which this op can reach. Emitting here too would double them
 * (#3530).
 */

import type { WriteOp, SyncResult } from '../types';
import { STORES, put, getAll } from '../../utils/indexedDB';
import { BASE_URL, jsonHeaders, classifyResponse, networkErrorResult } from '../httpHelpers';
import { clearLoneOdometer } from '../../utils/driveSegment';

export interface CreateManualVisitInput {
  storeId: number; // legacystoreid
  projectId: number;
  associateId: number;
  storeName: string;
  dteCall: string; // YYYY-MM-DD
  stCallTime: string; // YYYY-MM-DDTHH:MM:SS
  /** Drive-to-store leg. */
  driveToStartTime?: string;
  driveToEndTime?: string;
  driveOdoStart?: number;
  driveOdoEnd?: number;
  /** Return-drive leg (last visit of day on a retroactive entry). */
  driveBackStartTime?: string;
  driveBackEndTime?: string;
  driveBackOdoStart?: number;
  driveBackOdoEnd?: number;
  teamProject?: boolean;
  teamLeader?: boolean;
  notes?: string;
  /** Why this visit exists off the rep's schedule — the CallLog's own audit
   * column, never notes (checkout rewrites notes wholesale). */
  offScheduleReason?: string;
  gpsOverrideReason?: string;
}

export interface CreateManualVisitResponse {
  calllogid: number;
}

export const createManualVisitOp: WriteOp<
  CreateManualVisitInput,
  CreateManualVisitInput,
  CreateManualVisitResponse
> = {
  kind: 'createManualVisit',

  async applyLocal(input, localId) {
    await put(STORES.VISIT_TASK_SESSIONS, {
      uuid: localId,
      callLogId: null,
      status: 'in_progress',
      storeId: input.storeId,
      projectId: input.projectId,
      associateId: input.associateId,
      storeName: input.storeName,
      startTime: input.stCallTime,
      gpsOverrideReason: input.gpsOverrideReason ?? null,
    });
  },

  serialize(input, localId) {
    return {
      action: 'createManualVisit',
      endpoint: '/api/projects/calllog/',
      method: 'POST',
      payload: input,
      priority: 10, // a manual visit is the visit's anchor — drain first
      maxRetries: 10, // losing it is unacceptable
      dependsOn: null,
      relatedTaskUUID: localId,
      idempotencyKey: localId,
    };
  },

  async sync(row): Promise<SyncResult> {
    const input = row.payload as CreateManualVisitInput;
    const body: Record<string, unknown> = {
      store_id: input.storeId,
      project_id: input.projectId,
      associate_id: input.associateId,
      dtecall: input.dteCall,
      stcalltime: input.stCallTime,
      teamproject: input.teamProject ?? false,
      teamleader: input.teamLeader ?? false,
    };
    if (input.driveToStartTime) body.stdrivetime = input.driveToStartTime;
    if (input.driveToEndTime) body.eddrivetime = input.driveToEndTime;
    // A lone drive-to reading is a 400 (the serializer requires the pair
    // together); an equal pair is a valid zero-mile drive (#3618). Mirrors
    // the check-in path.
    const driveOdo = clearLoneOdometer(input.driveOdoStart, input.driveOdoEnd);
    if (driveOdo.startOdometer !== undefined) body.driveodostart = driveOdo.startOdometer;
    if (driveOdo.endOdometer !== undefined) body.driveodoend = driveOdo.endOdometer;
    if (input.driveBackStartTime) body.stdrivetimeback = input.driveBackStartTime;
    if (input.driveBackEndTime) body.eddrivetimeback = input.driveBackEndTime;
    if (input.driveBackOdoStart !== undefined) body.drivebackodostart = input.driveBackOdoStart;
    if (input.driveBackOdoEnd !== undefined) body.drivebackodoend = input.driveBackOdoEnd;
    if (input.notes) body.notes = input.notes;
    if (input.offScheduleReason) body.off_schedule_reason = input.offScheduleReason;
    if (input.gpsOverrideReason) body.gps_override_reason = input.gpsOverrideReason;

    let response: Response;
    try {
      response = await fetch(`${BASE_URL}${row.endpoint}`, {
        method: row.method,
        headers: jsonHeaders(row.idempotencyKey),
        body: JSON.stringify(body),
      });
    } catch (err) {
      return networkErrorResult(err);
    }

    const classified = await classifyResponse(response);
    if (classified) return classified;

    const data = await response.json().catch(() => ({}));
    const callLog = data.data || data;
    if (!callLog || typeof callLog.calllogid !== 'number') {
      return { status: 'failed', retriable: false, errorMessage: 'Server response missing calllogid' };
    }
    return { status: 'success', serverId: callLog.calllogid, retriable: false };
  },

  async reconcile(row, response) {
    if (typeof response?.calllogid !== 'number') return;
    const localId = row.relatedTaskUUID;
    if (!localId) return;
    const rows = (await getAll(STORES.VISIT_TASK_SESSIONS)) as Array<Record<string, unknown>>;
    const match = rows.find((r) => r.uuid === localId);
    if (!match) return;
    await put(STORES.VISIT_TASK_SESSIONS, { ...match, callLogId: response.calllogid });
  },

  dependsOn() {
    return null;
  },
};

export type CreateManualVisitOp = typeof createManualVisitOp;
